import type { NotificationProps } from '@mantine/notifications';
import { showNotification } from '@mantine/notifications';
import { createStyles } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';

import { SUPPORT_EMAIL } from '~/common/constants/support.const';
import { ButtonLink } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';

type ShowSplashNotificationProps = NotificationProps & {
  showSupportCTA?: boolean;
  autoClose?: boolean | number;
  disallowClose?: boolean;
  supportEmail?: string;
  ctaButtonText?: string;
};

const CS_BUTTON_WIDTH = 124;

const useStyles = createStyles(() => ({
  supportButtonContainer: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: CS_BUTTON_WIDTH,
  },
  supportButtonContainerAllowClose: {
    right: 20,
  },
  supportButtonContainerDisallowClose: {
    right: 16,
  },
  supportButtonText: {
    whiteSpace: 'nowrap',
  },
}));

export type { ShowSplashNotificationProps };

export const useShowSplashNotification = () => {
  const { t } = useTranslation('common');
  const { classes, cx } = useStyles();

  const showSplashNotification = ({
    title,
    message: rawMessage,
    supportEmail = SUPPORT_EMAIL.DEFAULT,
    ctaButtonText = t('support.contactSupport'),
    autoClose,
    disallowClose,
    showSupportCTA = false,
    ...props
  }: ShowSplashNotificationProps) => {
    const message =
      typeof rawMessage === 'string' && rawMessage.includes('\n') ? (
        <span style={{ whiteSpace: 'pre-line' }}>{rawMessage}</span>
      ) : (
        rawMessage
      );
    let finalAutoClose = autoClose;
    let finalDisallowClose = disallowClose;

    if (showSupportCTA) {
      finalAutoClose = false;
      if (disallowClose === undefined) {
        finalDisallowClose = true;
      }
    }

    return showNotification({
      title,
      message,
      ...props,
      // Apply autoClose and disallowClose with proper precedence
      ...(finalAutoClose !== undefined && { autoClose: finalAutoClose }),
      ...(finalDisallowClose !== undefined && { disallowClose: finalDisallowClose }),
      ...(showSupportCTA && {
        styles: {
          root: {
            '& .mantine-Notification-icon': {
              backgroundColor: 'transparent',
            },
            // Position close button in top right corner when both close button and support CTA are visible
            ...(!finalDisallowClose && {
              '& .mantine-Notification-closeButton': {
                position: 'absolute',
                top: 0,
                right: 0,
                zIndex: 1,
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'inherit',
                },
              },
            }),
          },
          body: {
            marginRight: CS_BUTTON_WIDTH + 24,
          },
        },
        icon: (
          <>
            <IconAlertCircle color="#EB612A" />
            <div
              className={cx(
                classes.supportButtonContainer,
                finalDisallowClose
                  ? classes.supportButtonContainerDisallowClose
                  : classes.supportButtonContainerAllowClose
              )}
            >
              <ButtonLink
                size="small"
                variant="secondary-fill"
                href={`mailto:${supportEmail}?subject=${t('support.emailSubject', { title })}&body=${t('support.emailBody', { message })}`}
              >
                <Text className={classes.supportButtonText} variant="headline-small" align="center">
                  {ctaButtonText}
                </Text>
              </ButtonLink>
            </div>
          </>
        ),
      }),
    });
  };

  return { showSplashNotification };
};
